import * as PIXI from "pixi.js";
import { UIColors, UIPositioning } from "../Settings/UITheme.js";
import NoteHelper from "../../HelperModules/NoteHelper.js";
import { MouseTypeContainer } from "./InteractiveContainer.js";
import { MouseClickType, TimelineMode } from "../Settings/Enums.js";
import { SequencerTimeline } from "../Sequencer/SequencerTimeline.js";
export class TrackTimelineEvent extends MouseTypeContainer {
    constructor(timeline, track, x, width, y, height) {
        super();
        this.borderLeft = 1;
        this.borderRight = 2;
        this.borderHeight = 2;
        this.paddingHeight = 2;
        this.selectedBorderWidth = 3;
        this._hovered = false;
        this._opacity = 1;
        this._hoveredColor = UIColors.trackEventHoveredColor;
        this.timeline = timeline;
        this.track = track;
        this._contentGraphics = new PIXI.Graphics();
        this._selectedGraphics = new PIXI.Graphics();
        this._selectedGraphics.zIndex = 1;
        this.addChild(this._contentGraphics, this._selectedGraphics);
        this.x = x + this.borderLeft;
        this.assignedWidth = Math.max(width - this.borderRight, 5);
        this.assignedHeight = height - this.borderHeight;
        this.y = y + this.borderHeight;
    }
    reinitialise(y, height) {
        let [x, width] = this.timeline.getTimelineEventXWidth(this.eventStartPosition, this.eventStartPosition + this.eventDuration);
        this.x = x + this.borderLeft;
        this.assignedWidth = Math.max(width - this.borderRight, 5);
        if (y != undefined) {
            this.y = y;
        }
        if (height != undefined) {
            this.assignedHeight = height;
        }
        this.redraw();
    }
    get leftBound() {
        return this.x;
    }
    get rightBound() {
        return this.x + this.width;
    }
    get hovered() {
        return this._hovered;
    }
    set hovered(value) {
        this._hovered = value;
        this.redrawSelected();
    }
    get hoveredColor() {
        return this._hoveredColor;
    }
    set hoveredColor(value) {
        if (value == null) {
            value = UIColors.trackEventHoveredColor;
        }
        this._hoveredColor = value;
        this.redrawSelected();
    }
    get opacity() {
        return this._opacity;
    }
    set opacity(value) {
        if (value < 0 || value > 1) {
            throw new RangeError("Opacity must be between 0 and 1.");
        }
        this._opacity = value;
        this.redraw();
    }
    destroy() {
        super.destroy({ children: true });
    }
    redraw() {
        this._contentGraphics.clear();
        this._contentGraphics.beginFill(UIColors.trackEventColor, this._opacity)
            .drawRect(0, 0, this.assignedWidth, this.assignedHeight)
            .endFill();
        this.redrawSelected();
    }
    redrawSelected() {
        this._selectedGraphics.clear();
        if (this.hovered) {
            this._selectedGraphics.beginFill(this._hoveredColor, this.opacity)
                .drawRect(0, 0, this.assignedWidth, this.assignedHeight)
                .endFill()
                .beginHole()
                .drawRect(this.selectedBorderWidth, this.selectedBorderWidth, this.assignedWidth - this.selectedBorderWidth * 2, this.assignedHeight - this.selectedBorderWidth * 2)
                .endHole();
        }
    }
    pointerDownHandler(event) {
        super.pointerDownHandler(event);
        if (this._mouseClickType == MouseClickType.None || !(this.timeline.timelineMode == TimelineMode.Edit)) {
            return;
        }
        event.stopPropagation();
        if (this._mouseClickType == MouseClickType.LeftClick) {
            this.hoveredColor = UIColors.trackEventSelectedColor;
        }
        else if (this._mouseClickType == MouseClickType.RightClick) {
            this.hoveredColor = UIColors.trackEventDeleteColor;
        }
        this._startXPosition = this.x;
        this._startYPosition = this.y;
        this._startPointerPosition = event.data.getLocalPosition(this.parent);
        this._snappedStartPointerPosition = this._startPointerPosition.clone();
        this._snappedStartPointerPosition.x = this.timeline.snapCoordinateToDragType(this._snappedStartPointerPosition.x);
    }
    pointerMoveHandler(event) {
        if (this._mouseClickType == MouseClickType.LeftClick && this.timeline.timelineMode == TimelineMode.Edit) {
            event.stopPropagation();
            let moveDelta = this.timeline.snapCoordinateToDragType(event.data.getLocalPosition(this.parent).x) - this._snappedStartPointerPosition.x;
            let newEventStart = this.timeline.metadata.positionQuarterNoteToBeats(this.eventStartPosition) + (moveDelta / this.timeline.beatWidth);
            if (newEventStart < 0) {
                moveDelta -= newEventStart * this.timeline.beatWidth;
            }
            this.x = this._startXPosition + moveDelta;
        }
    }
    pointerUpHandler(event) {
        super.pointerUpHandler(event);
        if (!this._destroyed) {
            this.hoveredColor = UIColors.trackEventHoveredColor;
            this._startXPosition = undefined;
            this._startYPosition = undefined;
            this._startPointerPosition = undefined;
            this._snappedStartPointerPosition = undefined;
        }
    }
    pointerUpClickHandler(event) {
        event.stopPropagation();
        if (this._mouseClickType == MouseClickType.LeftClick) {
            this.x = this._startXPosition;
            this.clickHandler();
        }
        else if (this.timeline.timelineMode == TimelineMode.Edit && this._mouseClickType == MouseClickType.RightClick) {
            this.deleteEvent();
        }
    }
    pointerUpDragHandler(event) {
        if (this.timeline.timelineMode == TimelineMode.Edit) {
            if (this._mouseClickType == MouseClickType.LeftClick) {
                let point = event.data.getLocalPosition(this.parent);
                let moveX = this.timeline.snapCoordinateToDragType(point.x) - this._snappedStartPointerPosition.x;
                let moveY = point.y - this._snappedStartPointerPosition.y;
                let newEventStart = this.timeline.metadata.positionQuarterNoteToBeats(this.eventStartPosition) + (moveX / this.timeline.beatWidth);
                if (newEventStart < 0) {
                    moveX -= newEventStart * this.timeline.beatWidth;
                }
                let dragDistance = new PIXI.Point(moveX, moveY);
                this.dragHandler(dragDistance);
            }
            else if (this._mouseClickType == MouseClickType.RightClick && this.mouseIsOver) {
                this.deleteEvent();
            }
        }
    }
    pointerOverHandler(event) {
        super.pointerOverHandler(event);
        this._hovered = true;
        this.redrawSelected();
    }
    pointerOutHandler(event) {
        super.pointerOutHandler(event);
        this._hovered = false;
        this.redrawSelected();
    }
    deleteEvent() {
        this.destroy();
    }
}
export class NoteGroupTimelineEvent extends TrackTimelineEvent {
    constructor(timeline, track, noteGroup, clickCallback) {
        let [x, width] = timeline.getTimelineEventXWidth(noteGroup[0], noteGroup[1]);
        let y = track.startY;
        let height = track.height;
        super(timeline, track, x, width, y, height);
        this._noteGroup = noteGroup;
        this._clickCallback = clickCallback;
        this.redraw();
    }
    get noteGroup() {
        return this._noteGroup;
    }
    get eventStartPosition() {
        return this._noteGroup[0];
    }
    get eventDuration() {
        return this._noteGroup[1] - this._noteGroup[0];
    }
    redraw() {
        super.redraw();
        this._notes = this.track.track.timeline.getEventsBetweenTimes(this._noteGroup[0], this._noteGroup[1]);
        this.setNotes(this._notes, this.track.track.highestPitch, this.track.track.lowestPitch, this._noteGroup);
    }
    setNotes(notes, highestNote, lowestNote, noteGroup) {
        let noteRange = NoteHelper.distanceBetweenNotes(highestNote, lowestNote) + 1;
        let metadata = this.timeline.metadata;
        let start = metadata.positionQuarterNoteToBeats(noteGroup[0]);
        let end = metadata.positionQuarterNoteToBeats(noteGroup[1]);
        let noteHeight = (this.track.height - this.paddingHeight * 2) / noteRange;
        let positionMap = position => {
            return (metadata.positionQuarterNoteToBeats(position) - start) / (end - start);
        };
        this._contentGraphics.beginFill(UIColors.trackEventHighlightColor, this._opacity);
        notes.forEach(note => {
            let startX = positionMap(note.startPosition) * this.assignedWidth;
            let endX = positionMap(note.endPosition) * this.assignedWidth;
            this._contentGraphics.drawRect(startX, NoteHelper.distanceBetweenNotes(highestNote, note.pitchString) * noteHeight + this.paddingHeight + NoteGroupTimelineEvent.noteMarginHeight, endX - startX - 2, noteHeight - NoteGroupTimelineEvent.noteMarginHeight * 2);
        });
        this._contentGraphics.endFill();
    }
    dragHandler(dragDistance) {
        let metadata = this.timeline.metadata;
        let noteGroup = Object.assign([], this._noteGroup);
        let beatChange = dragDistance.x / this.timeline.beatWidth;
        noteGroup[0] = metadata.positionBeatsToQuarterNote(metadata.positionQuarterNoteToBeats(noteGroup[0]) + beatChange);
        let quarterNoteChange = noteGroup[0] - this._noteGroup[0];
        noteGroup[1] = noteGroup[1] + quarterNoteChange;
        let groups = this.track.getNoteGroupsWithinTime(noteGroup[0], noteGroup[1]);
        let timePeriodClear = true;
        for (let i = 0; i < groups.length; i++) {
            if (!(groups[i][0] == this._noteGroup[0] && groups[i][1] == this._noteGroup[1])) {
                timePeriodClear = false;
                break;
            }
        }
        if (!timePeriodClear) {
            this.x = this._startXPosition;
        }
        else {
            this._notes.forEach(note => {
                this.track.track.timeline.removeEvent(note);
                note.startPosition = note.startPosition + quarterNoteChange;
                this.track.track.timeline.addEvent(note);
            });
            this.track.removeNoteGroup(this._noteGroup[0]);
            this.track.addNoteGroup(noteGroup[0], noteGroup[1]);
            this._noteGroup = noteGroup;
            this.reinitialise();
        }
    }
    deleteEvent() {
        this._notes.forEach(note => {
            this.track.track.removeNote(note);
        });
        delete this._notes;
        this.track.removeNoteGroup(this.noteGroup[0]);
        super.deleteEvent();
    }
    clickHandler() {
        this._clickCallback(this.track, this.eventStartPosition);
    }
}
NoteGroupTimelineEvent.noteMarginHeight = 2;
export class OneShotTimelineEvent extends TrackTimelineEvent {
    constructor(timeline, track, event) {
        let [x, width] = timeline.getTimelineEventXWidth(event.startPosition, event.endPosition);
        if (!track.displayActualWidth) {
            width = 0;
        }
        super(timeline, track, x, width, track.startY, track.height);
        this.event = event;
        this._modelEventRemoved = this._modelEventRemoved.bind(this);
        this.event.removed.addListener(this._modelEventRemoved);
        this.redraw();
    }
    get eventStartPosition() {
        return this.event.startPosition;
    }
    get eventDuration() {
        if (this.track.displayActualWidth) {
            return this.event.duration;
        }
        else {
            return 0;
        }
    }
    destroy() {
        this.event.removed.removeListener(this._modelEventRemoved);
        super.destroy();
    }
    deleteEvent() {
        this.track.track.timeline.removeEvent(this.event);
        super.deleteEvent();
    }
    _modelEventRemoved() {
        this.event.removed.removeListener(this._modelEventRemoved);
        super.deleteEvent();
    }
    dragHandler(dragDistance) {
        let beatChange = dragDistance.x / this.timeline.beatWidth;
        let newStartPosition = this.timeline.metadata.positionBeatsToQuarterNote(this.timeline.metadata.positionQuarterNoteToBeats(this.event.startPosition) + beatChange);
        let eventsInPeriod = this.track.track.timeline.getEventsBetweenTimes(newStartPosition, newStartPosition + this.event.duration);
        let timePeriodClear = true;
        for (let i = 0; i < eventsInPeriod.length; i++) {
            if (eventsInPeriod[i] != this.event) {
                timePeriodClear = false;
                break;
            }
        }
        if (!timePeriodClear) {
            this.x = this._startXPosition;
        }
        else {
            let eventIndex = this.track.track.timeline.getIndexOfEvent(this.event);
            this.track.track.timeline.editEvent(eventIndex, newStartPosition);
            this.reinitialise();
        }
    }
    clickHandler() {
    }
}
export class NoteTimelineEvent extends TrackTimelineEvent {
    constructor(timeline, track, event, y, height) {
        let [x, width] = timeline.getTimelineEventXWidth(event.startPosition, event.endPosition);
        super(timeline, track, x, width, y, height);
        this.event = event;
        this._modelEventRemoved = this._modelEventRemoved.bind(this);
        this.event.removed.addListener(this._modelEventRemoved);
        this.redraw();
    }
    get eventStartPosition() {
        return this.event.startPosition;
    }
    get eventDuration() {
        return this.event.duration;
    }
    pointerDownHandler(event) {
        super.pointerDownHandler(event);
        this._snappedStartPointerPosition.y = this._snappedStartPointerPosition.y - (this._snappedStartPointerPosition.y % SequencerTimeline.noteHeight) + SequencerTimeline.noteHeight;
    }
    pointerMoveHandler(event) {
        super.pointerMoveHandler(event);
        if (this._mouseClickType == MouseClickType.LeftClick) {
            let moveDelta = event.data.getLocalPosition(this.parent).y - this._snappedStartPointerPosition.y;
            let oldNoteNumber = NoteHelper.noteStringToNoteNumber(this.event.pitchString);
            let noteYPosition = Math.min(this.timeline.offsetContentHeight - UIPositioning.timelineHeaderHeight, Math.max(0, oldNoteNumber * SequencerTimeline.noteHeight - moveDelta));
            this._lastNoteNumber = Math.floor(noteYPosition / SequencerTimeline.noteHeight);
            this.y = this._startYPosition - (this._lastNoteNumber - oldNoteNumber) * SequencerTimeline.noteHeight;
        }
    }
    destroy() {
        this.event.removed.removeListener(this._modelEventRemoved);
        super.destroy();
    }
    deleteEvent() {
        this.track.removeEvent(this.event);
        super.deleteEvent();
    }
    _modelEventRemoved() {
        this.event.removed.removeListener(this._modelEventRemoved);
        super.deleteEvent();
    }
    dragHandler(dragDistance) {
        let beatChange = dragDistance.x / this.timeline.beatWidth;
        let newNotePitch = NoteHelper.noteNumberToNoteString(this._lastNoteNumber);
        let newStartPosition = this.timeline.metadata.positionBeatsToQuarterNote(this.timeline.metadata.positionQuarterNoteToBeats(this.event.startPosition) + beatChange);
        let eventsInPeriod = this.track.track.timeline.getEventsBetweenTimes(newStartPosition, newStartPosition + this.event.duration);
        let timePeriodClear = true;
        for (let i = 0; i < eventsInPeriod.length; i++) {
            if (eventsInPeriod[i] != this.event && eventsInPeriod[i].pitchString == newNotePitch) {
                timePeriodClear = false;
                break;
            }
        }
        if (!timePeriodClear) {
            this.x = this._startXPosition;
            this.y = this._startYPosition;
        }
        else {
            this.track.editEvent(this.event, newStartPosition, newNotePitch);
            this.reinitialise();
        }
    }
    clickHandler() {
    }
}
//# sourceMappingURL=TrackTimelineEvent.js.map